/**
 * Chat Types
 * Shared types for the AI Chat Assistant feature.
 * Used by both server (chat service, routes) and client (stores, hooks, components).
 */

// Betting card types live in their own leaf module (chatTypes size limit); imported for the union.
import type { DirectionPriorCardData, PlayerPropHitRateCardData } from './chatBettingCards';
import type { LeagueKey } from './league';
import type { PreseasonWindow } from './statsChart';

// =============================================================================
// Stream Events (Server → Client via SSE)
// =============================================================================

/** Types of events sent from server to client during chat streaming */
export type ChatStreamEventType =
  | 'text_delta'
  | 'tool_start'
  | 'tool_result'
  | 'data_card'
  | 'navigate'
  | 'preference_update'
  | 'clear_chat'
  | 'confirmation'
  | 'pending_followup'
  | 'chips'
  | 'compaction'
  | 'conversation_saved'
  | 'done'
  | 'error';

export interface ChatTextDeltaEvent {
  type: 'text_delta';
  text: string;
}

export interface ChatToolStartEvent {
  type: 'tool_start';
  toolName: string;
  toolCallId: string;
  /** Tool input arguments — included for server-side persistence/debugging */
  toolInput?: Record<string, unknown>;
}

/**
 * Categorization for tool execution failures, mirrored from server's ToolErrorCategory.
 * Surfaced to the UI so different failure modes can render distinct states.
 */
export type ChatToolErrorCategory =
  | 'transient'
  | 'validation'
  | 'permission'
  | 'business'
  | 'not_found';

export interface ChatToolResultEvent {
  type: 'tool_result';
  toolCallId: string;
  toolName: string;
  dataCard?: DataCard;
  /** Summary text returned to the LLM — included for server-side persistence/debugging */
  toolOutput?: string;
  /** True when the tool call failed. Absent or false on success. */
  isError?: boolean;
  /** Failure category for UI rendering decisions. Set when isError is true. */
  errorCategory?: ChatToolErrorCategory;
}

export interface ChatDataCardEvent {
  type: 'data_card';
  card: DataCard;
}

export interface ChatNavigateEvent {
  type: 'navigate';
  screen: string;
  context?: Record<string, unknown>;
}

export interface ChatPreferenceUpdateEvent {
  type: 'preference_update';
  key: 'sportsbook' | 'theme';
  value: string;
}

export interface ChatClearChatEvent {
  type: 'clear_chat';
}

/** Structured details for generate_picks confirmations */
export interface GeneratePicksConfirmationDetails {
  mode: string;
  games: Array<{ id: string; matchup: string; startTime: string }> | 'all';
  totalGamesAvailable: number;
  markets: string[];
  presetName: string | null;
  enabledFactors: number;
  factorLimit: number;
  wasClamped: boolean;
  bannedPlayers: string[];
  sportsbook: string;
  quotaUsed: number;
  quotaLimit: number;
  quotaRemaining: number;
}

export interface ChatConfirmationEvent {
  type: 'confirmation';
  confirmationId: string;
  action: string;
  details: string;
  structuredDetails?: GeneratePicksConfirmationDetails;
  toolCallId: string;
}

/**
 * Emitted when the agent has paused on a write-tool confirmation but is
 * about to continue with brief text-only follow-up (no more tool calls)
 * while the user reviews the modal. UI uses this to show a "Continuing
 * while waiting for confirmation" indicator next to the pending modal.
 */
export interface ChatPendingFollowupEvent {
  type: 'pending_followup';
  /** Matches the toolCallId of the active ChatConfirmationEvent. */
  toolCallId: string;
}

export interface ChatChipsEvent {
  type: 'chips';
  chips: string[];
}

export interface ChatDoneEvent {
  type: 'done';
}

export interface ChatErrorEvent {
  type: 'error';
  message: string;
  code?: 'timeout' | 'rate_limit' | 'server_error' | 'not_authorized';
}

/** Sent when conversation history is compacted to stay within context limits */
export interface ChatCompactionEvent {
  type: 'compaction';
  summary: string;
  removedCount: number;
}

export interface ChatConversationSavedEvent {
  type: 'conversation_saved';
  conversationId: string;
  messageCount: number;
}

export type ChatStreamEvent =
  | ChatTextDeltaEvent
  | ChatToolStartEvent
  | ChatToolResultEvent
  | ChatDataCardEvent
  | ChatNavigateEvent
  | ChatPreferenceUpdateEvent
  | ChatClearChatEvent
  | ChatConfirmationEvent
  | ChatPendingFollowupEvent
  | ChatChipsEvent
  | ChatCompactionEvent
  | ChatConversationSavedEvent
  | ChatDoneEvent
  | ChatErrorEvent;

// =============================================================================
// Data Cards (Rich UI cards embedded in chat messages)
// =============================================================================

export type DataCardType =
  | 'player_stats'
  | 'leg_analysis'
  | 'parlay_summary'
  | 'injury_report'
  | 'live_parlay'
  | 'analytics_summary'
  | 'leaderboard_rank'
  | 'line_movement'
  | 'notification_list'
  | 'tier_comparison'
  | 'settlement_explanation'
  | 'parlay_comparison'
  | 'daily_briefing'
  | 'team_stats'
  | 'back_to_back'
  | 'player_prop_hit_rate'
  | 'direction_prior'
  | 'chart';

// -----------------------------------------------------------------------------
// Per-card data interfaces
// -----------------------------------------------------------------------------

/** Stat averages keyed by stat abbreviation (pts, reb, ast, fg3m, stl, blk, etc.) */
export type StatAverages = Record<string, number>;

/** A single recent game entry for player stats */
export interface PlayerRecentGame {
  opponent?: string;
  pts?: number;
  reb?: number;
  ast?: number;
  fg3m?: number;
  stl?: number;
  blk?: number;
  dnp?: boolean;
  isPreseason?: boolean;
  [key: string]: unknown;
}

/** Head-to-head stats for a player vs a specific team */
export interface PlayerH2HStats {
  gamesPlayed: number;
  avgPoints?: number | string;
  avgRebounds?: number | string;
  avgAssists?: number | string;
  avgThrees?: number | string;
  avgSteals?: number | string;
  avgBlocks?: number | string;
}

export interface PlayerStatsCardData {
  playerName: string;
  team?: string | null;
  playerId?: number | null;
  position?: string | null;
  season?: StatAverages | null;
  recentGames?: PlayerRecentGame[] | null;
  l5?: StatAverages | null;
  l10?: StatAverages | null;
  l20?: StatAverages | null;
  /** Present when this is a player-vs-team (H2H) card */
  opponentTeamAbbr?: string;
  h2h?: PlayerH2HStats;
  /** Lets the client resolve team abbreviations in the player's own league. */
  leagueKey: LeagueKey;
  /** Present only in the NBA preseason window. */
  preseasonWindow?: PreseasonWindow;
}

/** A single scored leg from get_scoring_breakdown */
export interface ScoredLeg {
  playerName: string;
  statType: string;
  line: number;
  overUnder: string;
  teamAbbr: string;
  opponentAbbr: string;
  score: number;
  probability: number;
  verdict: string;
  factors: Array<{ factor: string; impact: string; details?: string }>;
  /** Absent on cards saved before the server set it. */
  leagueKey?: LeagueKey;
}

export interface LegAnalysisCardData {
  legs: ScoredLeg[];
}

/** A single analyzed leg from analyze_parlay_deep */
/** analyze_parlay_deep's verdict for a leg it has no season average to judge against. */
export const NO_STATS_VERDICT = 'NO STATS';

export interface AnalyzedLeg {
  playerName: string;
  statType: string;
  line: number;
  overUnder: string;
  teamAbbr?: string;
  opponentAbbr?: string;
  /** Absent when the player has no season average for the stat; the verdict is then NO_STATS_VERDICT. */
  seasonAverage?: number;
  edge?: number;
  verdict: string;
  hasInjuries: boolean;
  odds?: string;
  /** Absent on cards saved before the server set it. */
  leagueKey?: LeagueKey;
}

/** A generated parlay from the pick generation pipeline */
export interface GeneratedParlayCard {
  name?: string | null;
  legs: GeneratedLegCard[];
  totalOdds?: string | null;
  odds?: string | null;
  confidence?: string | null;
  reasoning?: string | null;
  [key: string]: unknown;
}

/** A single leg within a generated parlay card */
export interface GeneratedLegCard {
  player?: string | null;
  team?: string | null;
  opponentTeam?: string | null;
  /** True when `team` plays at home. */
  isHomeGame?: boolean | null;
  position?: string | null;
  statType?: string | null;
  prop?: string | null;
  propType?: string | null;
  marketKey?: string | null;
  line?: number | null;
  overUnder?: string | null;
  odds?: string | null;
  leagueKey?: string | null;
  explanation?: { summary?: string; [key: string]: unknown } | null;
  [key: string]: unknown;
}

export interface ParlaySummaryCardData {
  /** Present when displaying generated picks */
  picks?: GeneratedParlayCard[];
  /** Present when displaying deep analysis results */
  legs?: AnalyzedLeg[];
  overallVerdict?: string;
  legCount?: number;
  strongCount?: number;
  estimatedTotalOdds?: string;
  /** Generation metadata */
  generatedAt?: string;
  cached?: boolean;
  gamesCount?: number;
  quotaRemaining?: number;
  parlayScores?: unknown;
  /** Streaming: index of the parlay being streamed */
  parlayIndex?: number;
  /** The generation that produced these picks; its legs carry their prediction row ids. */
  generationId?: string;
  /** The finished generation, which replaces the cards streamed while it ran. */
  final?: boolean;
}

export interface InjuryEntry {
  playerName: string;
  status: string;
  injury?: string;
  teamAbbr: string;
  /** Absent on cards saved before the server set it. */
  leagueKey?: LeagueKey;
}

export interface InjuryReportCardData {
  teamAbbr: string;
  injuries: InjuryEntry[];
}

/** A parlay leg as stored in the database (used in live_parlay, comparison, settlement cards) */
export interface ParlayLegSummary {
  player?: string;
  team?: string | null;
  opponentTeam?: string | null;
  playerPosition?: string | null;
  statType?: string;
  prop?: string;
  line?: number | string | null;
  overUnder?: string | null;
  odds?: string;
  hit?: boolean | null;
  actualValue?: number | string | null;
  margin?: number | string | null;
  leagueKey?: string;
  [key: string]: unknown;
}

/** A tracked parlay summary for list/detail views */
export interface TrackedParlayCard {
  id?: number;
  name?: string | null;
  result?: string | null;
  legCount?: number;
  stake?: string | null;
  totalOdds?: string | null;
  sportsbook?: string | null;
  legs?: ParlayLegSummary[];
  createdAt?: string | null;
  /** Present on shared parlays */
  sharedBy?: string;
  sharedAt?: string;
  sharedWith?: string;
  [key: string]: unknown;
}

export interface LiveParlayCardData {
  /** Single parlay detail */
  parlay?: TrackedParlayCard;
  /** List of parlays */
  parlays?: TrackedParlayCard[];
}

/** Insights sub-object for analytics summary */
export interface AnalyticsInsights {
  totalBets?: number;
  totalWins?: number;
  totalLosses?: number;
  totalWagered?: string;
  totalProfit?: string;
  winRate?: string | number;
  roi?: string | number;
  totalLegs?: number;
  totalLegHits?: number;
  legHitRate?: string | number;
  overHitRate?: string | number;
  underHitRate?: string | number;
  bestPlayers?: Array<{ player: string; hitRate?: string; hits?: number; total?: number }>;
  worstPlayers?: Array<{ player: string; hitRate?: string; hits?: number; total?: number }>;
  bestProps?: Array<{ prop: string; hitRate?: string; hits?: number; total?: number }>;
  worstProps?: Array<{ prop: string; hitRate?: string; hits?: number; total?: number }>;
  [key: string]: unknown;
}

/** Market insights day entry */
export interface MarketInsightDay {
  date: string;
  marketCondition?: string;
  insight?: string;
  legStats?: { total?: number; hitRate?: number; overPct?: number };
}

export interface AnalyticsSummaryCardData {
  /** User insights variant */
  insights?: AnalyticsInsights | null;
  /** Market insights variant */
  type?: 'market_insights';
  days?: MarketInsightDay[];
}

/** Leaderboard entry */
export interface LeaderboardEntry {
  rank?: number;
  user: { id?: string; displayName?: string | null };
  stats: { roi: number; winRate: number; wins: number; losses: number; totalBets: number };
}

export interface LeaderboardRankCardData {
  rank?: number | null;
  percentile?: number | null;
  stats?: { roi: number; winRate: number; wins: number; losses: number; totalBets: number } | null;
  totalUsers: number;
  top5: LeaderboardEntry[];
}

/** Odds book entry for line movement / odds comparison */
export interface OddsBookEntry {
  game?: string;
  bookmaker: string;
  name?: string;
  line: number;
  overPrice?: number;
  underPrice?: number;
}

export interface LineMovementCardData {
  playerName: string;
  statType?: string;
  books?: OddsBookEntry[];
  /** Alternative props variant */
  availableProps?: Record<string, Array<{ line: number; bookmaker: string }>>;
}

export interface NotificationEntry {
  title?: string;
  type?: string;
  body?: string;
  message?: string;
  readAt?: string | Date | null;
  createdAt?: string | Date;
  [key: string]: unknown;
}

export interface NotificationListCardData {
  notifications: NotificationEntry[];
  unreadCount: number;
}

export interface TierInfo {
  name: string;
  price: string;
  features: string[];
}

export interface TierComparisonCardData {
  tiers: TierInfo[];
}

/** A settlement leg with optional AI prediction data */
export interface SettlementLeg extends ParlayLegSummary {
  /** Absent on cards saved before the server set it. */
  leagueKey?: LeagueKey;
  voidReason?: string | null;
  aiReasoning?: string | null;
  predictedValue?: number | null;
  aiEdge?: string | null;
  aiConfidence?: string | null;
  edgePct?: number | null;
  riskFactors?: unknown;
  confidenceBreakdown?: {
    statisticalEdge?: number | null;
    matchupFactor?: number | null;
    consistency?: number | null;
    gameScript?: number | null;
    overall?: number | null;
  };
  /** Closing line value: positive means you beat the closing line */
  clv?: number | null;
  /** The closing line captured near game start */
  closingLine?: number | null;
}

export interface SettlementCardData {
  parlayId: number;
  result: string;
  totalOdds?: string | null;
  parlayReasoning?: string | null;
  hasAiData: boolean;
  legs: SettlementLeg[];
}

/** A parlay in a comparison view */
export interface ComparisonParlay {
  parlayId: number;
  result?: string;
  totalOdds?: string | null;
  confidence?: string | number | null;
  legCount: number;
  legs: ParlayLegSummary[];
}

export interface ParlayComparisonCardData {
  parlay1: ComparisonParlay;
  parlay2: ComparisonParlay;
  overlappingPlayers: string[];
}

export interface BriefingGame {
  away: string;
  home: string;
  startTime: string;
}

export interface BriefingRecentResult {
  parlayId: number;
  result: string;
  legCount: number;
}

export interface DailyBriefingCardData {
  games: BriefingGame[];
  pendingCount: number;
  recentResults: BriefingRecentResult[];
  winRateTrend?: {
    last7d: number;
    last7dSample?: number;
    overall: number;
    overallSample: number;
  };
}

export interface TeamStatsCardData {
  teamAbbr: string;
  /** Display label, e.g. "WNBA". */
  league?: string;
  /** Absent on cards saved before it was added; renderers fall back to parsing `league`. */
  leagueKey?: LeagueKey;
  /** Regular-season W-L when recordScope says so; every game this season on older cards. */
  recentRecord: string;
  recordScope?: 'regular_season';
  /** Playoff W-L when the team has played playoff games this season. */
  playoffRecord?: string;
  totalGames: number;
  /** Most recent first. Cards saved before 2026-09-29 carry entries without scores, which render as nothing. */
  recentGames?: Array<{
    date?: string;
    opponentAbbr?: string;
    isHome?: boolean;
    teamScore?: number;
    opponentScore?: number;
    won?: boolean;
  }>;
}

export interface BackToBackCardData {
  /** Specific team check */
  team?: { abbr: string; isB2B: boolean; playedYesterday: boolean; lastGameDate: string | null };
  /** All teams playing today */
  b2bTeams?: string[];
  restedTeams?: string[];
  /** Per-prop-type hit rate edges for teams ON B2B, sorted by biggest edge */
  edges: Array<{ label: string; b2b: number; rested: number; diff: number }>;
  totalSamples: number;
  /** Per-prop-type hit rate edges for teams FACING a B2B opponent */
  oppEdges?: Array<{ label: string; b2b: number; rested: number; diff: number }>;
  totalOppSamples?: number;
}

/** A single trend point */
export interface ChartPoint {
  label: string;
  value: number;
  sampleSize?: number;
}

/** A single bar entry */
export interface ChartBar {
  label: string;
  value: number;
  sampleSize?: number;
  highlight?: boolean;
}

/** A pie segment */
export interface ChartSegment {
  label: string;
  value: number;
  percentage: number;
}

/** A multi-stat metric */
export interface ChartMetric {
  label: string;
  current: number;
  previous?: number;
  format: string;
}

export interface ChartCardData {
  chartType: 'trend_line' | 'bar_comparison' | 'pie_breakdown' | 'multi_stat';
  title: string;
  subtitle?: string;
  /** trend_line */
  points?: ChartPoint[];
  referenceLine?: { value: number; label: string };
  valueFormat?: string;
  /** bar_comparison */
  bars?: ChartBar[];
  /** pie_breakdown */
  segments?: ChartSegment[];
  /** multi_stat */
  metrics?: ChartMetric[];
  context?: { metric?: string; dateRange?: string; groupBy?: string };
  followUpChips?: string[];
}

// -----------------------------------------------------------------------------
// Discriminated union DataCard
// -----------------------------------------------------------------------------

export type DataCard =
  | { type: 'player_stats'; data: PlayerStatsCardData }
  | { type: 'leg_analysis'; data: LegAnalysisCardData }
  | { type: 'parlay_summary'; data: ParlaySummaryCardData }
  | { type: 'injury_report'; data: InjuryReportCardData }
  | { type: 'live_parlay'; data: LiveParlayCardData }
  | { type: 'analytics_summary'; data: AnalyticsSummaryCardData }
  | { type: 'leaderboard_rank'; data: LeaderboardRankCardData }
  | { type: 'line_movement'; data: LineMovementCardData }
  | { type: 'notification_list'; data: NotificationListCardData }
  | { type: 'tier_comparison'; data: TierComparisonCardData }
  | { type: 'settlement_explanation'; data: SettlementCardData }
  | { type: 'parlay_comparison'; data: ParlayComparisonCardData }
  | { type: 'daily_briefing'; data: DailyBriefingCardData }
  | { type: 'team_stats'; data: TeamStatsCardData }
  | { type: 'back_to_back'; data: BackToBackCardData }
  | { type: 'player_prop_hit_rate'; data: PlayerPropHitRateCardData }
  | { type: 'direction_prior'; data: DirectionPriorCardData }
  | { type: 'chart'; data: ChartCardData };

/** Extract the data type for a specific card type */
export type DataCardDataFor<T extends DataCardType> = Extract<DataCard, { type: T }>['data'];

// =============================================================================
// Chat Messages (Client-side conversation state)
// =============================================================================

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'summary';
  content: string;
  dataCards?: DataCard[];
  toolCalls?: ChatToolCallInfo[];
  timestamp: number;
}

export interface ChatToolCallInfo {
  id: string;
  name: string;
  status: 'running' | 'complete' | 'error';
  startedAt?: number;
  completedAt?: number;
  /** Tool input arguments (for debugging/replay). Captured on tool_start. */
  input?: Record<string, unknown>;
  /** Tool result summary returned to the LLM (for debugging). Captured on tool_result. */
  output?: string;
  /** Failure category, set when the tool returned a structured error. */
  errorCategory?: ChatToolErrorCategory;
}

// =============================================================================
// Init Context (Client → Server on chat open)
// =============================================================================

export interface ChatInitContext {
  currentScreen: string;
  screenData?: Record<string, unknown>;
  timeOfDay: 'morning' | 'afternoon' | 'evening' | 'night';
  displayName: string;
}

// =============================================================================
// API Request/Response types
// =============================================================================

export interface ChatMessageRequest {
  message: string;
  conversationHistory: ChatMessage[];
  initContext?: ChatInitContext;
  conversationId?: string;
}

export interface ChatInitResponse {
  welcomeMessage: string;
  chips: string[];
  context: {
    recentRecord?: { wins: number; losses: number; pending: number };
    liveCount?: number;
    tonightGameCount?: number;
    streak?: { type: 'win' | 'loss'; count: number } | null;
  };
}

export interface ChatConfirmRequest {
  confirmationId: string;
  confirmed: boolean;
  conversationHistory: ChatMessage[];
  conversationId?: string;
}

// =============================================================================
// Conversation Persistence
// =============================================================================

/** Server-side conversation record returned by GET /api/chat/conversation */
export interface ChatConversation {
  id: string;
  messages: ChatMessage[];
  quickChips: string[];
  lastActivityAt: number;
}

/** Response from GET /api/chat/conversation */
export interface ChatConversationResponse {
  conversation: ChatConversation | null;
}

// =============================================================================
// Tool Metadata
// =============================================================================

export type ChatToolCategory = 'read' | 'write';

export interface ChatToolMeta {
  name: string;
  category: ChatToolCategory;
  requiresConfirmation: boolean;
}

// =============================================================================
// Chat UI Constants
// =============================================================================

/** Default prompts shown in the empty chat state (before any messages). */
export const CHAT_DEFAULT_PROMPTS = [
  'Daily briefing',
  'Generate AI picks',
  "Who's injured tonight?",
  'Show my stats',
  "Show tonight's games",
  'What can you do?',
] as const;

/** Subtitle shown above default prompts in the empty chat state. */
export const CHAT_EMPTY_STATE_SUBTITLE =
  "Ask me anything about your parlays, players, or tonight's games.";
